import axios from 'axios'
import { readHasViewedFromApi } from './portalRequestNotifications.js'
import { formatBillingDocumentDateInputValue, formatBillingCalendarDate } from './portalBillingDocumentDates.js'
import {
  isQuoteConverted,
  isPurchaseHistoryInvoiceUnpaid,
  isPurchaseHistoryQuoteEntry,
} from './portalBillingDocuments.js'
import { instructorPortalOrderSourceFields } from './portalOrderSource.js'

const jsonHeaders = { 'Content-Type': 'application/json' }

function requestConfig() {
  return { withCredentials: true, headers: jsonHeaders }
}

function formatRequestDate(iso) {
  if (!iso) return null
  const formatted = formatBillingCalendarDate(iso, { fallback: '' })
  return formatted || String(iso)
}

/**
 * @param {string | null | undefined} iso
 */
export function formatExtensionRequestDate(iso) {
  return formatRequestDate(iso) ?? '—'
}

/**
 * @param {Record<string, unknown>} request
 */
export function extensionRequestDateLabel(request) {
  return request?.documentType === 'invoice' ? 'Due date' : 'Expiration'
}

/**
 * @param {Record<string, unknown>} request
 */
export function extensionRequestDatesLine(request) {
  if (!request) return '—'
  const isInvoice = request.documentType === 'invoice'
  const current = formatExtensionRequestDate(
    isInvoice ? request.currentDueAt : request.currentValidUntil
  )
  const requested = formatExtensionRequestDate(
    isInvoice ? request.requestedDueAt : request.requestedValidUntil
  )
  const approved = formatExtensionRequestDate(
    isInvoice ? request.approvedDueAt : request.approvedValidUntil
  )
  if (String(request.status ?? '').toLowerCase() === 'approved' && approved !== '—') {
    return `${current} → ${requested} → ${approved}`
  }
  return `${current} → ${requested}`
}

/**
 * @param {Array<Record<string, unknown>>} existing
 * @param {Record<string, unknown>} data
 */
export function mergeBillingDocumentExtensionRequestsFromResponse(data, existing = []) {
  const rows = data?.requests ?? (data?.request ? [data.request] : null)
  if (!Array.isArray(rows) || rows.length === 0) return null
  const mapped = rows.map(mapBillingDocumentExtensionRequestFromApi)
  const byId = new Map(existing.map((row) => [String(row.id), row]))
  for (const row of mapped) {
    byId.set(String(row.id), row)
  }
  return [...byId.values()].sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  )
}

/**
 * @param {unknown} err
 * @param {string} fallback
 */
export function billingDocumentExtensionApiErrorMessage(err, fallback) {
  const data = err && typeof err === 'object' ? err.response?.data : null
  const msg = data?.message ?? data?.error
  if (typeof msg === 'string' && msg.trim()) return msg.trim()
  return fallback
}

/**
 * @param {Record<string, unknown>} row
 */
export function mapBillingDocumentExtensionRequestFromApi(row) {
  const id = row.id ?? row._id ?? row.requestId
  const documentType = String(row.documentType ?? '').toLowerCase() === 'invoice' ? 'invoice' : 'quote'
  return {
    id,
    organizationId: row.organizationId ?? null,
    documentId: row.documentId ?? row.billingDocumentId ?? null,
    documentNumber: String(row.documentNumber ?? '').trim(),
    documentType,
    status: String(row.status ?? 'pending').toLowerCase(),
    currentValidUntil: row.currentValidUntil ?? row.previousValidUntil ?? null,
    currentDueAt: row.currentDueAt ?? row.previousDueAt ?? null,
    requestedValidUntil: row.requestedValidUntil ?? null,
    requestedDueAt: row.requestedDueAt ?? null,
    approvedValidUntil: row.approvedValidUntil ?? null,
    approvedDueAt: row.approvedDueAt ?? null,
    note: String(row.note ?? row.requestNote ?? '').trim(),
    responseNote: String(row.responseNote ?? '').trim(),
    submittedBy: row.submittedBy ?? null,
    reviewedBy: row.reviewedBy ?? null,
    date: formatRequestDate(row.createdAt) ?? '',
    responseDate: formatRequestDate(row.respondedAt) ?? '',
    respondedAt: row.respondedAt ?? null,
    createdAt: row.createdAt ?? null,
    hasViewed: readHasViewedFromApi(row),
  }
}

/**
 * @param {string} organizationId
 */
export async function fetchOrganizationBillingDocumentExtensionRequests(organizationId) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/get-organization-billing-document-extension-requests`,
    { organizationId },
    requestConfig()
  )
  const rows = data?.requests ?? []
  return (Array.isArray(rows) ? rows : []).map(mapBillingDocumentExtensionRequestFromApi)
}

/**
 * @param {{
 *   organizationId: string,
 *   documentId: string,
 *   documentType: 'quote' | 'invoice',
 *   requestedValidUntil?: string,
 *   requestedDueAt?: string,
 *   note?: string,
 *   submittedBy?: Record<string, unknown>,
 * }} payload
 */
export async function postRequestBillingDocumentExtension(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/request-billing-document-extension`,
    { ...payload, ...instructorPortalOrderSourceFields() },
    requestConfig()
  )
  return data
}

/**
 * Uninvoiced quote — extension can be requested any time (not only when expired).
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null | undefined} [docForEntry]
 */
export function canRequestQuoteExtension(entry, docForEntry = null) {
  const doc = docForEntry ?? entry?.billingDocument ?? entry
  const isQuote =
    doc?.documentType === 'quote' ||
    entry?.documentType === 'quote' ||
    entry?.type === 'Estimate/Quote'
  if (!isQuote) return false
  if (doc && isQuoteConverted(doc)) return false
  if (!doc && entry && isPurchaseHistoryQuoteEntry(entry) && isQuoteConverted(entry)) {
    return false
  }
  const status = String(entry?.status ?? doc?.status ?? '').toLowerCase()
  if (['deleted', 'void', 'voided', 'converted'].includes(status)) return false
  return Boolean(doc?.id ?? doc?._id ?? entry?.documentId ?? entry?.id)
}

/**
 * Unpaid invoice — due date extension can be requested any time.
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null | undefined} [docForEntry]
 */
export function canRequestInvoiceExtension(entry, docForEntry = null) {
  const doc = docForEntry ?? entry?.billingDocument ?? entry
  const isInvoice =
    doc?.documentType === 'invoice' ||
    entry?.documentType === 'invoice' ||
    entry?.type === 'Invoice'
  if (!isInvoice) return false
  return isPurchaseHistoryInvoiceUnpaid(entry ?? doc) && Boolean(
    doc?.id ?? doc?._id ?? entry?.documentId ?? entry?.id
  )
}

/**
 * @param {Array<Record<string, unknown>>} requests
 * @param {string | number} documentId
 * @param {'quote' | 'invoice'} documentType
 */
export function findPendingExtensionRequestForDocument(
  requests,
  documentId,
  documentType
) {
  const id = String(documentId ?? '').trim()
  if (!id) return null
  return (
    (requests ?? []).find(
      (row) =>
        String(row.documentId) === id &&
        row.documentType === documentType &&
        String(row.status ?? 'pending').toLowerCase() === 'pending'
    ) ?? null
  )
}

/**
 * @param {Record<string, unknown>} request
 */
export function extensionRequestRequestedDateLabel(request) {
  if (!request) return '—'
  if (request.documentType === 'invoice') {
    return formatRequestDate(request.requestedDueAt) ?? '—'
  }
  return formatRequestDate(request.requestedValidUntil) ?? '—'
}

/**
 * @param {'quote' | 'invoice'} documentType
 * @param {string | Date | null | undefined} currentDate
 */
export function defaultExtensionRequestDateValue(documentType, currentDate = new Date()) {
  const base = currentDate instanceof Date ? currentDate : new Date()
  const next = new Date(base)
  next.setDate(next.getDate() + 30)
  return formatBillingDocumentDateInputValue(next)
}
